import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Pencil, Plus, RefreshCw, Trash2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { hasPermission } from "../lib/utils/permissions";
import { useAuthStore } from "../context/AuthContext";
import useToast from "../hooks/useToast";
import apiClient from "../lib/utils/network-client";
import { DeleteConfirmationButton } from "../components/common/ConfirmationDialog";
import AdminListToolbar from "../components/common/AdminListToolbar";
import ListPagination from "../components/common/ListPagination";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";


const Journals = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { showErrorToast, showSuccessToast } = useToast();
  const canCreate = hasPermission(user, "journals", "create");
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [volumeFilter, setVolumeFilter] = useState("all");
  const [durationFilter, setDurationFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const volumes = useMemo(() => [...new Set(items.map((item) => String(item.volume || "")).filter(Boolean))]
    .sort((a, b) => b.localeCompare(a, undefined, { numeric: true })), [items]);
  const durations = useMemo(() => [...new Set(items.map((item) => String(item.duration || "")).filter(Boolean))]
    .sort((a, b) => b.localeCompare(a, undefined, { numeric: true })), [items]);

  const filteredItems = useMemo(() => {
    const query = search.trim().toLowerCase();
    return items.filter((item) => {
      const matchesVolume = volumeFilter === "all" || String(item.volume) === volumeFilter;
      const matchesDuration = durationFilter === "all" || String(item.duration) === durationFilter;
      const matchesSearch = !query || [item.volume, item.number, item.duration]
        .some((value) => String(value || "").toLowerCase().includes(query));
      return matchesVolume && matchesDuration && matchesSearch;
    });
  }, [durationFilter, items, search, volumeFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / pageSize));
  const paginatedItems = filteredItems.slice((page - 1) * pageSize, page * pageSize);

  useEffect(() => {
    setPage((currentPage) => Math.min(currentPage, totalPages));
  }, [totalPages]);

  const fetchItems = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get("/journals");
      setItems(response.data?.data?.journals || []);
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to load journals");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const handleDelete = async (item) => {
    try {
      await apiClient.delete(`/journals/${item.id}`);
      showSuccessToast("Journal deleted successfully");
      fetchItems();
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Unable to delete journal");
    }
  };

  return (
    <div className="max-w-[1600px] space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">GCSMC Journal of Medical Sciences</h1>
          <p className="mt-1 text-sm font-medium text-slate-500">Manage published issues, full-issue PDFs, and section-wise articles shown on the public website.</p>
        </div>
        <div className="flex gap-3">
          <Button type="button" variant="outline" className="rounded-lg" onClick={fetchItems}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
          {canCreate && (

            <Button type="button" className="rounded-lg" onClick={() => navigate("/journals/new")}>
            <Plus className="mr-2 h-4 w-4" />
            Add Journal Issue
          </Button>

          )}
        </div>
      </div>

      <AdminListToolbar
        searchValue={search}
        onSearchChange={(value) => {
          setSearch(value);
          setPage(1);
        }}
        searchPlaceholder="Search volume, number, duration..."
        searchLabel="Search journals"
        filteredCount={filteredItems.length}
        totalCount={items.length}
        itemLabel="journal issues"
        hasActiveFilters={Boolean(search) || volumeFilter !== "all" || durationFilter !== "all"}
        onReset={() => {
          setSearch("");
          setVolumeFilter("all");
          setDurationFilter("all");
          setPage(1);
        }}
      >
        <Select value={volumeFilter} onValueChange={(value) => {
          setVolumeFilter(value);
          setPage(1);
        }}>
          <SelectTrigger className="min-w-40" aria-label="Filter journals by volume">
            <SelectValue placeholder="All volumes" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All volumes</SelectItem>
            {volumes.map((volume) => <SelectItem key={volume} value={volume}>Volume {volume}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={durationFilter} onValueChange={(value) => {
          setDurationFilter(value);
          setPage(1);
        }}>
          <SelectTrigger className="min-w-44" aria-label="Filter journals by duration">
            <SelectValue placeholder="All durations" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All durations</SelectItem>
            {durations.map((duration) => <SelectItem key={duration} value={duration}>{duration}</SelectItem>)}
          </SelectContent>
        </Select>
      </AdminListToolbar>

      <div className="overflow-hidden rounded-lg border border-white/60 bg-white/80 shadow-sm backdrop-blur-xl">
        {isLoading ? (
          <div className="py-12 text-center text-sm font-medium text-slate-500">Loading journals...</div>
        ) : filteredItems.length === 0 ? (
          <div className="py-12 text-center text-sm text-slate-500">
            {items.length === 0 ? "No journals found." : "No journal issues match your filters."}
          </div>
        ) : (
          <>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100">
              <thead className="bg-slate-50/80">
                <tr>
                  <th className="px-5 py-4 text-left text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Volume</th>
                  <th className="px-5 py-4 text-left text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Number</th>
                  <th className="px-5 py-4 text-left text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Duration</th>
                  <th className="px-5 py-4 text-left text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Entries</th>
                  <th className="px-5 py-4 text-right text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {paginatedItems.map((item) => (
                  <tr key={item.id}>
                    <td className="px-5 py-4 text-sm font-semibold text-slate-800">{item.volume}</td>
                    <td className="px-5 py-4 text-sm text-slate-700">{item.number}</td>
                    <td className="px-5 py-4 text-sm text-slate-700">{item.duration}</td>
                    <td className="px-5 py-4 text-sm text-slate-600">
                      <div>Editorial: {item.entry_counts?.editorial || 0}</div>
                      <div>Review Article: {item.entry_counts?.review_article || 0}</div>
                      <div>Original Article: {item.entry_counts?.original_article || 0}</div>
                      <div>Case Report: {item.entry_counts?.case_report || 0}</div>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex justify-end gap-2">
                        <Button type="button" variant="outline" className="rounded-lg" onClick={() => navigate(`/journals/${item.id}/edit`)}>
                          <Pencil className="mr-2 h-4 w-4" />
                          Edit
                        </Button>
                        <DeleteConfirmationButton onConfirm={() => handleDelete(item)}>
<Button type="button" variant="outline" className="rounded-lg border-red-200 text-red-600 hover:bg-red-50">
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete
                        </Button>
</DeleteConfirmationButton>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ListPagination
            page={page}
            pageSize={pageSize}
            totalItems={filteredItems.length}
            onPageChange={setPage}
            onPageSizeChange={(value) => {
              setPageSize(value);
              setPage(1);
            }}
            itemLabel="journal issues"
          />
          </>
        )}
      </div>
    </div>
  );
};

export default Journals;
